<template>
  <DraggableDiv
    class="pdp-thumbnail-carousel-mobile relative flex items-center gap-2 justify-center"
    :style="{ height: carouselHeight }"
    @drag-right="handleNextClick"
    @drag-left="handlePrevClick"
  >
    <CarouselArrowButton
      v-if="hasArrows"
      :is-clickable="hasPrev"
      :is-horizontal="true"
      :is-previous-button="true"
      @click="handlePrevClick"
    />
    <div
      class="pdp-thumbnail-carousel-mobile__container relative mt-auto mb-auto"
      :style="{ width: carouselArea }"
    >
      <div
        class="flex flex-row gap-2"
        :style="carouselStyles"
      >
        <ThumbnailCarouselItem
          v-for="(item, index) in sliderItems"
          :key="index"
          :item="item"
          :size="thumbnailDimension"
          @click="handleItemClick"
        />
      </div>
    </div>
    <CarouselArrowButton
      v-if="hasArrows"
      :is-clickable="hasNext"
      :is-horizontal="true"
      :is-previous-button="false"
      @click="handleNextClick"
    />
  </DraggableDiv>
</template>

<script lang="ts">

import type { PropType } from 'vue';

import type { ThumbnailItem } from '~~/types/components/ThumbnailCarousel/ThumbnailItem';

export default defineComponent({
  name: 'PdpThumbnailCarouselMobile',

  props: {
    sliderItems: {
      type: Array as PropType<Array<ThumbnailItem>>,
      required: true,
    },
  },
  emits: ['item-click'],
  setup(props, { emit }) {

    const currentTranslateX = ref(0);
    const thumbnailDimension = 40;
    const thumbnailGap = 8;
    const currentIndex = ref(0);
    const maxSliderItemsDisplayed = 5;

    const visibleItemsCount = computed(() => Math.min(maxSliderItemsDisplayed, props.sliderItems.length));

    const { handlePrevClickHelper, handleNextClickHelper, carouselArea } = useThumbnailCarouselClicks({
      thumbnailGap,
      thumbnailDimension,
      visibleItemsCount: visibleItemsCount.value,
      sliderItemsLength: props.sliderItems.length,
    });

    const carouselHeight = computed(() => `${thumbnailDimension}px`);

    const carouselStyles = computed(() => ({
      transform: `translateX(${currentTranslateX.value}px)`,
      transition: 'transform 0.5s ease',
    }));

    const hasPrev = computed(() => currentIndex.value > 0);
    const hasNext = computed(() => (currentIndex.value + visibleItemsCount.value) < props.sliderItems.length);

    const hasArrows = computed(() => props.sliderItems.length > 5);

    const handlePrevClick = () => {

      const clickResult = handlePrevClickHelper(currentIndex.value);

      if (clickResult) {

        currentIndex.value = clickResult.newCurrentIndex;
        currentTranslateX.value = clickResult.newPlacement;
      
      }
    
    };

    const handleNextClick = () => {

      const clickResult = handleNextClickHelper(currentIndex.value);

      if (clickResult) {

        currentIndex.value = clickResult.newCurrentIndex;
        currentTranslateX.value = clickResult.newPlacement;
      
      }
    
    };

    const handleItemClick = (item: ThumbnailItem) => {

      emit('item-click', { index: item.originalIndex });
    
    };

    return {
      handlePrevClick,
      handleNextClick,
      handleItemClick,
      carouselArea,
      carouselStyles,
      hasPrev,
      hasNext,
      hasArrows,
      carouselHeight,
      thumbnailDimension,
    };
  
  },
});

</script>

<style lang="scss" scoped>
.pdp-thumbnail-carousel-mobile {
  &__container {
    overflow-x: hidden;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
